import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { AxiosError } from "axios";
import * as microsoftTeams from "@microsoft/teams-js";
import { Checkbox, Flex, Button, Tooltip, Input } from "@fluentui/react-northstar";
import { RobotIcon, SaveIcon } from "@fluentui/react-icons-northstar";
import find from "lodash/find";
import DatepickerWithLabel from "../components/pickers/datepickerWithLabel";
import InputWithLabel from "../components/common/inputWithLabel";
import ProjectPicker from "../components/pickers/projectPicker";
import Alert from "../components/common/alert";
import IReport from "../interfaces/IReport";
import IProject from "../interfaces/IProject";
import ICustomer from "../interfaces/ICustomer";

import { useAuth } from "../contexts/authContext";
import useClearAuthIssue from "../common/useClearAuthIssue";
import { useApp } from "../contexts/appContext";
import { createReport, updateReport } from "../services/dataService";
import { useCustomers, useInvalidateData, useMe, useProjects, useReport } from "../queries";
import { addHours, getDefaultStartDate, isValidDate, parseDateToDate } from "../common/dateOperations";
import { numberToString, stringToNumber } from "../common/numberOperations";
import useSaveShortcut from "../common/useSaveShortcut";
import { hasValue, projectTimeToString, parseJwt, removeHtmlTags } from "../common/textOperations";
import styles from "../common.module.scss";
import { getShowOnlyMyProjects, setShowOnlyMyProjects } from "../services/settingsService";
import { blokaceProject, dovolenaProject, temporaryProject } from "../constants";

const ReportForm: React.FunctionComponent<{ copy?: boolean }> = ({ copy }) => {
    const { id } = useParams<{ id: string }>();
    document.title = !id ? "New" : copy ? "New" : "Edit";

    const params = new URLSearchParams(window.location.search);
    const initProjectId = params.get("initProjectId");

    const [errorMessage, setErrorMessage] = useState("");
    const [authIssue, setAuthIssue] = useState(true);
    const [viewOwn, setViewOwn] = useState(getShowOnlyMyProjects());
    const [hours, setHours] = useState("0,5");
    const [aiSavedHours, setAiSavedHours] = useState("0");
    const [selectedProject, setSelectedProject] = useState<IProject>();
    const [selectedProjectId, setSelectedProjectId] = useState(initProjectId ?? "");
    const [reportId, setReportId] = useState("");
    const [subject, setSubject] = useState("");

    const [start, setStart] = useState(getDefaultStartDate());
    const [end, setEnd] = useState(addHours(getDefaultStartDate(), 0.5));

    const [completed, setCompleted] = useState(false);
    const [overhead, setOverhead] = useState(false);
    const [requestedByCustomer, setRequestedByCustomer] = useState(false);
    const [overtime, setOvertime] = useState(false);
    const [shadowing, setShadowing] = useState(false);
    const [extendedHours, setExtendedHours] = useState(false);
    const [description, setDescription] = useState("");
    const [kilometers, setKilometers] = useState<string>("");
    const [location, setLocation] = useState("");
    const [customer, setCustomer] = useState<ICustomer | null>();
    const [login, setLogin] = useState("");
    const [isTempReport, setIsTempReport] = useState(false);
    const [holidayTimeIssue, setHolidayTimeIssue] = useState(false);

    // The report may arrive after the first render, but the form must be filled only once
    // so that a background refresh never throws away what the user is typing
    const filledFromReport = useRef(false);

    const { state: authState } = useAuth();
    const { accessToken: token } = authState;
    useClearAuthIssue(authIssue, () => { setErrorMessage(""); setAuthIssue(false); });
    const history = useHistory();
    const { state: appState, dispatch } = useApp();

    const { report } = useReport(id);
    const { data: projectsData } = useProjects();
    const { data: customersData } = useCustomers();
    const { data: me } = useMe();
    const { invalidateReports, invalidateProjects } = useInvalidateData();

    const projects = projectsData ?? [];
    const customers = customersData ?? [];
    const showAiSavedHours = me?.showAiSavedHours ?? false;
    const nda = customer?.nda ? customer.nda.replace(/\n/g, "<br />") : null;
    const projectIsMine = selectedProject?.owner.login === login;

    useEffect(() => {
        if (!report || filledFromReport.current) { return; }
        filledFromReport.current = true;

        if (id) {
            setReportId(report.id);
            setSubject(report.subject);
            setStart(report.start);
            setEnd(report.end);
            setHours(numberToString(report.durationInMinutes / 60));
            setCompleted(report.state === 1);
            setOverhead(report.overhead);
            setRequestedByCustomer(report.requestedByCustomer);
            setOvertime(report.overtime);
            setShadowing(report.shadowing);
            setExtendedHours(report.extendedHours);
            setDescription(removeHtmlTags(report.description ?? ""));
            setKilometers(report.kilometers ? report.kilometers.toString() : "");
            setLocation(report.location ?? "");
            setSelectedProjectId(report.project.id);
            setAiSavedHours(report.aiSavedHours ? numberToString(report.aiSavedHours) : "0");
        }

        if (copy) {
            if (report.project.subject != dovolenaProject && report.project.subject != blokaceProject) {
                setStart(getDefaultStartDate());
                setEnd(addHours(getDefaultStartDate(), 0.5));
                setHours("0,5");
            }

            setCompleted(false);
            setOverhead(false);
            setOvertime(false);
            setExtendedHours(false);
        }
    }, [report, id, copy]);

    // A copied report can point at a project that is no longer offered to the user
    useEffect(() => {
        if (!copy || !report || !projectsData) { return; }

        const project = find(projects, (x: IProject) => x.id === report.project.id);
        if (!project) {
            setSelectedProjectId("");
        }
    }, [copy, report, projectsData]);

    useEffect(() => {
        if (!token) { return; }
        const data = parseJwt(token);
        setLogin(data.upn);
    }, [token]);

    useEffect(() => {
        const isTemp = selectedProject?.subject === temporaryProject;
        setIsTempReport(isTemp);
        if (isTemp) {
            setCompleted(false);
        }
    }, [selectedProject]);

    useEffect(() => {
        const time = stringToNumber(hours);
        const holidayIssue: boolean = selectedProject?.subject == dovolenaProject && (time != 0 && time != 4 && time != 8);
        setHolidayTimeIssue(holidayIssue);
    }, [hours, selectedProject]);

    useEffect(() => {
        const project = find(projects, (x: IProject) => x.id === selectedProjectId);
        setSelectedProject(project);

        if (project) {
            const foundCustomer = find(customers, (x: ICustomer) => x.id === project.customerId);
            setCustomer(foundCustomer);
        }
    }, [selectedProjectId, projectsData, customersData]);

    const toggleLoading = (loading: boolean) => {
        dispatch({ type: "SET_LOADER", show: loading });
    };

    const send = async () => {
        if (!token) { return; }
        setErrorMessage("");
        setAuthIssue(false);

        const km = parseInt(kilometers, 0);
        const aiSavedHoursNumber = parseFloat(aiSavedHours?.replace(",", ".") ?? 0);
        const data: IReport = {
            id: reportId,
            subject,
            start,
            end,
            durationInMinutes: Math.trunc(stringToNumber(hours) * 60),
            project: {
                id: selectedProjectId,
                customerId: "",
                number: "",
                owner: { login: "", viewOnlyOwnReports: false },
                parentIsClosed: false,
                plannedHours: 0,
                remainingHours: 0,
                reportedHours: 0,
                subject: ""
            },
            overhead,
            requestedByCustomer,
            overtime,
            shadowing,
            extendedHours,
            carpool: false,
            state: completed ? 1 : 3, // 1 - completed, 3 - scheduled
            description,
            kilometers: isNaN(km) ? 0 : km,
            location,
            link: "",
            aiSavedHours: isNaN(aiSavedHoursNumber) ? 0 : aiSavedHoursNumber
        };

        toggleLoading(true);
        try {
            if (!reportId || copy) {
                const forceSyncWithOutlook = selectedProject?.subject === dovolenaProject
                    || selectedProject?.subject === blokaceProject;
                await createReport(token, data, forceSyncWithOutlook);
            } else {
                await updateReport(token, data);
            }
            toggleLoading(false);
            invalidateReports();
            invalidateProjects();
            history.push("/");
        } catch (error) {
            const err: AxiosError = error as AxiosError;
            toggleLoading(false);
            if (err.response?.status === 401) {
                setErrorMessage("Přístupový token vypršel.");
                setAuthIssue(true);
            } else {
                setErrorMessage(err.message);
                console.log(err.response?.data);
            }
        }
    };

    const calculateEnd = (startDate: Date, durationInHours: number) => {
        const endDate = addHours(startDate, durationInHours);
        setEnd(addHours(startDate, durationInHours));
        setCompleted(endDate <= new Date() && !isTempReport);
    };

    const startChanged = (value: Date) => {
        // Manual input in the datepicker can produce an invalid date, keep the last valid one
        if (!isValidDate(value)) { return; }

        setStart(value);
        calculateEnd(value, stringToNumber(hours));
    };

    const hoursChanged = (_: any, data: any) => {
        setHours(data.value);
        calculateEnd(start, stringToNumber(data.value));
    };

    const setAiSavedHoursChanged = (_: any, data: any) => {
        setAiSavedHours(data.value);
    };

    const shadowingChanged = (_: any, data: any) => {
        const { checked } = data;
        setShadowing(checked);
        setOverhead(checked);
    };

    const projectUpdated = (data: any) => {
        if (data.result && selectedProject) {
            setSelectedProject(data.result);
            invalidateProjects();
        }
    };

    const viewOwnChanged = (_: any, data: any) => {
        setViewOwn(data.checked);
        setShowOnlyMyProjects(data.checked);
    };

    const showModal = () => {
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Úprava případu",
            size: {
                height: 400,
                width: 360
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/project/edit-popup/${selectedProject?.id}`
        };

        microsoftTeams.dialog.url.open(dialogInfo, projectUpdated);
    };

    const showGenerateModal = () => {
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Generátor výkazu",
            size: {
                height: 580,
                width: 600
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/report/generate/${selectedProject?.language ?? "CZ"}`
        };

        microsoftTeams.dialog.url.open(dialogInfo, (data: any) => setSubject(data.result));
    };

    const formIsValid = () => {
        let isValid = true;
        if (isTempReport) {
            isValid = hasValue(description);
        }

        return (
            isValid &&
            !isNaN(stringToNumber(hours)) &&
            hasValue(subject) &&
            hasValue(start) &&
            hasValue(end) &&
            hasValue(hours) &&
            hasValue(selectedProjectId) &&
            !holidayTimeIssue
        );
    };

    useSaveShortcut(send, formIsValid() && !appState.showLoader);

    const renderProjectTime = () => {
        if (selectedProject && selectedProject.plannedHours + selectedProject.remainingHours > 0) {
            return (
                <div>
                    <small>{projectTimeToString(selectedProject?.plannedHours, selectedProject?.remainingHours)}</small>
                </div>
            );
        }

        return null;
    };

    const renderEditProjectButton = <Button content="Upravit případ" size="small" onClick={showModal} />;

    return (
        <div>
            <Flex column gap="gap.medium" className={styles.formBody}>
                <Flex column gap="gap.smaller">
                    <small>Popis výkazu</small>
                    <Flex gap="gap.smaller">
                        <Flex.Item grow>
                            <Input value={subject} onChange={(_: any, data: any) => setSubject(data.value)} maxLength={328} fluid />
                        </Flex.Item>
                        <Flex.Item>
                            <Tooltip
                                trigger={<Button text iconOnly icon={<RobotIcon />} onClick={showGenerateModal} />}
                                position-align="before-center"
                                content="Generovat text výkazu"
                            />
                        </Flex.Item>
                    </Flex>
                </Flex>
                <InputWithLabel label="Interní poznámka" value={description} onChange={(_: any, data: any) => setDescription(data.value)} maxLength={4000} />

                <DatepickerWithLabel label="Začátek" date={parseDateToDate(start)} onChange={startChanged} selectTextOnClick />
                <DatepickerWithLabel label="Konec" date={parseDateToDate(end)} disabled />
                <InputWithLabel value={hours} label="Celková práce v hodinách" onChange={hoursChanged} selectTextOnClick />
                {showAiSavedHours &&
                    <InputWithLabel value={aiSavedHours} label="AI-úspora času" onChange={setAiSavedHoursChanged} selectTextOnClick />
                }

                <Flex column gap="gap.smaller">
                    <Flex gap="gap.small">
                        <small style={{ paddingTop: "6px" }}>Případ</small>
                        <Checkbox label="Jen moje případy" checked={viewOwn} onChange={viewOwnChanged} />
                        {projectIsMine && window.innerWidth < 450 &&
                            <Flex.Item push>
                                {renderEditProjectButton}
                            </Flex.Item>
                        }
                        {projectIsMine && window.innerWidth > 450 && renderEditProjectButton}
                    </Flex>
                    <div>
                        <ProjectPicker selectedProjectId={selectedProjectId} viewOnlyOwn={viewOwn} projectSelected={(x: string) => setSelectedProjectId(x)} />
                        {renderProjectTime()}
                        {selectedProject?.language &&
                            <div>
                                <small>Jazyk vykazování: {selectedProject.language}</small>
                            </div>
                        }
                        {selectedProject?.projectType &&
                            <div>
                                <small>Typ objednávky: {selectedProject.projectType}</small>
                            </div>
                        }
                        {nda &&
                            <div>
                                <small dangerouslySetInnerHTML={{ __html: nda }}></small>
                            </div>
                        }
                        {customer?.aiRestriction &&
                            <div style={{ marginTop: 5 }}>
                                <Alert type="warning">AI Restriction viz. informace výše.</Alert>
                            </div>
                        }
                    </div>
                    {selectedProject?.parentIsClosed &&
                        <Alert type="warning">Na tento případ není možné vykázat, protože jeho nadřazený případ je uzavřený.</Alert>
                    }
                    {isTempReport &&
                        <Alert type="info">Pro tento případ je nutné vyplnit interní poznámku s informací o zadavateli a zákazníkovi.</Alert>
                    }
                    {holidayTimeIssue &&
                        <Alert type="warning">Dovolenou lze vykázat pouze na půlden či celý den.</Alert>
                    }
                </Flex>

                <Checkbox label="Dokončeno" checked={completed} onChange={(_: any, data: any) => setCompleted(data.checked)} disabled={isTempReport} />
                <Checkbox label="Režie" checked={overhead} onChange={(_: any, data: any) => setOverhead(data.checked)} disabled={shadowing} />
                <div>
                    <Checkbox label="Mimo pracovní dobu na přání zákazníka" checked={requestedByCustomer} onChange={(_: any, data: any) => setRequestedByCustomer(data.checked)} />
                    <div style={{ paddingLeft: "34px" }}><small>Zaškrtněte v případě, že zákazník požadoval, aby byla práce vykonána mimo pracovní dobu.</small></div>
                </div>
                <Checkbox label="Přesčas" checked={overtime} onChange={(_: any, data: any) => setOvertime(data.checked)} />
                <div>
                    <Checkbox label="Extended hours" checked={extendedHours} onChange={(_: any, data: any) => setExtendedHours(data.checked)} />
                    <div style={{ paddingLeft: "34px" }}><small>Zaškrtněte v případě, že práce byla vykonávaná v rozšířené pracovní době.</small></div>
                </div>
                <div>
                    <Checkbox label="Shadowing" checked={shadowing} onChange={shadowingChanged} />
                    <div style={{ paddingLeft: "34px" }}><small>Zaškrtává osoba, která se učila/poslouchala.</small></div>
                </div>

                <InputWithLabel value={kilometers} label="Kilometry (zaokrouhleno)" onChange={(_: any, data: any) => setKilometers(data.value.replace(/\D/g, ""))} />
                <InputWithLabel value={location} label="Místo" onChange={(_: any, data: any) => setLocation(data.value)} />
                {errorMessage && <Alert type="error" authIssue={authIssue}>{errorMessage}</Alert>}
            </Flex>

            <div className={styles.footer} style={{ background: appState.backgroundColor }}>
                <Flex gap="gap.small" className={styles.centerContainer}>
                    <Flex.Item push>
                        <Button content="Uložit" primary icon={<SaveIcon outline />} disabled={!formIsValid() || appState.showLoader} loading={appState.showLoader} onClick={send} />
                    </Flex.Item>
                </Flex>
            </div>
        </div>
    );
};

export default ReportForm;
